import type { Coordinate } from '@shared/models/geography.model';
import type { Building } from '@shared/models/design/building-design.model';
import type { Storey } from '@shared/models/design/storey.model';
import type { Point2D } from '@shared/models/geometry.model';
import type { Map } from 'mapbox-gl';
import { convertMetricOffsetToCoordinate } from '@shared/helpers/geometry/coordinates.helpers';
import { getStoreyElevationRange } from '@shared/helpers/design/storey.helpers';
import {
  computeBuildingFitZoom,
  EARTH_CIRCUMFERENCE_M_PER_PX_AT_Z0,
  getBuildingFootprintBbox,
  type BuildingFootprintBbox,
} from './building-fit-zoom.helpers';
import { projectLngLatAtElevation } from './map.helpers';

/** Left edge hidden by Design chrome when the map extends behind the panel. */
export const getMapViewportLeftInset = (map: Map): number => {
  const container = map.getContainer?.();
  const content = container?.closest('[data-design-map-content]');
  if (!content || !container) return 0;
  return Math.max(
    0,
    content.getBoundingClientRect().left -
      container.getBoundingClientRect().left,
  );
};

/** Fraction of the shorter map side the footprint should fill when recentering. */
export const MEDIUM_BUILDING_FOCUS_PADDING_FACTOR = 0.35;

/** Minimum free viewport fraction on every side after Shift+Z. */
export const BUILDING_VOLUME_FOCUS_MARGIN_FRACTION = 0.2;

/** Compensates for perspective changing slightly as the target zoom is applied. */
const BUILDING_VOLUME_FOCUS_PROJECTION_SAFETY_FACTOR = 0.95;

/** Viewport inset (0–0.5) so the bar does not flicker at the edges. */
const VIEWPORT_EDGE_INSET_FRACTION = 0.02;

export type MapViewportBounds = {
  getWest: () => number;
  getEast: () => number;
  getSouth: () => number;
  getNorth: () => number;
};

export type BuildingFocusCamera = {
  /** Map center as `[lng, lat]`. */
  center: [number, number];
  zoom: number;
};

export type BuildingVolumeFocusCamera = {
  center: [number, number];
  zoom: number;
  offset: [number, number];
  padding: { top: number; bottom: number; left: number; right: number };
};

/**
 * Camera that tightly frames the rendered 3D volume inside the visible map.
 * The current projection supplies the footprint rotation, pitch and height
 * distortion; changing zoom then scales that projected extent uniformly.
 */
export const getBuildingVolumeFocusCamera = ({
  map,
  location,
  storeyGroups,
  rightViewportInsetPx = 0,
  marginFraction = BUILDING_VOLUME_FOCUS_MARGIN_FRACTION,
}: {
  map: Map;
  location: Coordinate;
  storeyGroups: readonly (readonly Storey[])[];
  rightViewportInsetPx?: number;
  marginFraction?: number;
}): BuildingVolumeFocusCamera | undefined => {
  const storeys = storeyGroups.flat();
  const footprint = getBuildingFootprintBbox(storeys);
  if (!footprint) return undefined;

  const projectedPoints: Point2D[] = [];
  storeyGroups.forEach((group) => {
    const buildingStoreys = [...group];
    buildingStoreys.forEach((storey) => {
      const { bottom, top } = getStoreyElevationRange(buildingStoreys, storey);
      storey.shapes.forEach((shape) => {
        shape.points.forEach((point) => {
          const coordinate = convertMetricOffsetToCoordinate(location, point);
          projectedPoints.push(
            projectLngLatAtElevation(map, coordinate, bottom),
            projectLngLatAtElevation(map, coordinate, top),
          );
        });
      });
    });
  });
  if (!projectedPoints.length) return undefined;

  const xs = projectedPoints.map(([x]) => x);
  const ys = projectedPoints.map(([, y]) => y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const projectedWidth = maxX - minX;
  const projectedHeight = maxY - minY;
  if (!(projectedWidth > 0) || !(projectedHeight > 0)) return undefined;

  const canvas = map.getCanvas();
  const rightInset = Math.max(0, rightViewportInsetPx);
  const leftInset = getMapViewportLeftInset(map);
  const visibleWidth = canvas.clientWidth - leftInset - rightInset;
  const visibleHeight = canvas.clientHeight;
  const usableFraction = 1 - 2 * marginFraction;
  if (!(visibleWidth > 0) || !(visibleHeight > 0) || !(usableFraction > 0)) {
    return undefined;
  }

  const scale =
    Math.min(
      (visibleWidth * usableFraction) / projectedWidth,
      (visibleHeight * usableFraction) / projectedHeight,
    ) * BUILDING_VOLUME_FOCUS_PROJECTION_SAFETY_FACTOR;
  if (!(scale > 0) || !Number.isFinite(scale)) return undefined;

  const centerMetric: Point2D = [
    (footprint.minX + footprint.maxX) / 2,
    (footprint.minY + footprint.maxY) / 2,
  ];
  const centerCoordinate = convertMetricOffsetToCoordinate(
    location,
    centerMetric,
  );
  const anchor = projectLngLatAtElevation(map, centerCoordinate, 0);
  const projectedCenter: Point2D = [(minX + maxX) / 2, (minY + maxY) / 2];

  return {
    center: [centerCoordinate.lng, centerCoordinate.lat],
    zoom: map.getZoom() + Math.log2(scale),
    offset: [
      (anchor[0] - projectedCenter[0]) * scale,
      (anchor[1] - projectedCenter[1]) * scale,
    ],
    padding: { top: 0, bottom: 0, left: leftInset, right: rightInset },
  };
};

/**
 * Vertical screen offset that puts the middle of the rendered building height
 * on the camera center instead of anchoring its footprint at the center.
 * Storeys stay grouped per building so sibling heights are not accumulated.
 */
export const getBuildingElevationCenterOffset = ({
  map,
  center,
  storeyGroups,
}: {
  map: Map;
  center: Coordinate;
  storeyGroups: readonly (readonly Storey[])[];
}): number => {
  const elevationRanges = storeyGroups.flatMap((storeys) => {
    const buildingStoreys = [...storeys];
    return buildingStoreys.map((storey) =>
      getStoreyElevationRange(buildingStoreys, storey),
    );
  });
  if (!elevationRanges.length) return 0;

  const bottom = Math.min(...elevationRanges.map((range) => range.bottom));
  const top = Math.max(...elevationRanges.map((range) => range.top));
  const groundY = projectLngLatAtElevation(map, center, 0)[1];
  const bottomY = projectLngLatAtElevation(map, center, bottom)[1];
  const topY = projectLngLatAtElevation(map, center, top)[1];

  return groundY - (bottomY + topY) / 2;
};

/**
 * Screen offset that centers a selected building at a future pitch while
 * preserving the current zoom and bearing. Uses the same local-meter geometry
 * as the renderer so pitch and centering can share one camera animation.
 */
export const getBuildingTargetPitchCenterOffset = ({
  storeys,
  center,
  latitudeDegrees,
  zoom,
  bearingDegrees,
  pitchDegrees,
}: {
  storeys: readonly Storey[];
  center: Point2D;
  latitudeDegrees: number;
  zoom: number;
  bearingDegrees: number;
  pitchDegrees: number;
}): [number, number] => {
  const buildingStoreys = [...storeys];
  const bearing = (bearingDegrees * Math.PI) / 180;
  const pitch = (pitchDegrees * Math.PI) / 180;
  const cosBearing = Math.cos(bearing);
  const sinBearing = Math.sin(bearing);
  const cosPitch = Math.cos(pitch);
  const sinPitch = Math.sin(pitch);
  const projected: Point2D[] = [];

  buildingStoreys.forEach((storey) => {
    const { bottom, top } = getStoreyElevationRange(buildingStoreys, storey);
    storey.shapes.forEach((shape) => {
      shape.points.forEach(([x, y]) => {
        const dx = x - center[0];
        const dy = y - center[1];
        const screenX = dx * cosBearing + dy * sinBearing;
        const groundScreenY = (-dx * sinBearing + dy * cosBearing) * cosPitch;
        projected.push(
          [screenX, groundScreenY - bottom * sinPitch],
          [screenX, groundScreenY - top * sinPitch],
        );
      });
    });
  });
  if (!projected.length) return [0, 0];

  const minX = Math.min(...projected.map(([x]) => x));
  const maxX = Math.max(...projected.map(([x]) => x));
  const minY = Math.min(...projected.map(([, y]) => y));
  const maxY = Math.max(...projected.map(([, y]) => y));
  const pixelsPerMeter =
    2 ** zoom /
    (EARTH_CIRCUMFERENCE_M_PER_PX_AT_Z0 *
      Math.cos((latitudeDegrees * Math.PI) / 180));

  return [
    (-(minX + maxX) / 2) * pixelsPerMeter,
    (-(minY + maxY) / 2) * pixelsPerMeter,
  ];
};

type BuildingFocusCameraInput = {
  location: Coordinate;
  storeys: readonly Storey[];
  containerWidth: number;
  containerHeight: number;
  /** @default {@link MEDIUM_BUILDING_FOCUS_PADDING_FACTOR} */
  paddingFactor?: number;
};

/**
 * Footprints of the selected building plus every sibling. Out-of-view and
 * Show-to-fit treat the site as one combined outline.
 */
export const getSiteFootprintStoreys = (
  selectedStoreys: readonly Storey[],
  otherBuildings: readonly Building[],
): Storey[] => [
  ...selectedStoreys,
  ...otherBuildings.flatMap((building) => building.storeys),
];

/**
 * Geographic axis-aligned bounds of a local-meter footprint around {@link location}.
 */
export const getBuildingFootprintLngLatBounds = (
  location: Coordinate,
  bbox: BuildingFootprintBbox,
): { west: number; east: number; south: number; north: number } => {
  const corners = [
    convertMetricOffsetToCoordinate(location, [bbox.minX, bbox.minY]),
    convertMetricOffsetToCoordinate(location, [bbox.maxX, bbox.minY]),
    convertMetricOffsetToCoordinate(location, [bbox.maxX, bbox.maxY]),
    convertMetricOffsetToCoordinate(location, [bbox.minX, bbox.maxY]),
  ];
  let west = Infinity;
  let east = -Infinity;
  let south = Infinity;
  let north = -Infinity;
  for (const corner of corners) {
    west = Math.min(west, corner.lng);
    east = Math.max(east, corner.lng);
    south = Math.min(south, corner.lat);
    north = Math.max(north, corner.lat);
  }
  return { west, east, south, north };
};

/**
 * True when the site footprint does not intersect the (slightly inset) map viewport.
 * Pass every building's storeys so a visible sibling is not treated as out of view.
 * Empty designs (no vertices) are treated as not out-of-view — nothing to recenter on.
 */
export const isBuildingOutsideMapViewport = (
  location: Coordinate,
  storeys: readonly Storey[],
  viewport: MapViewportBounds,
): boolean => {
  const bbox = getBuildingFootprintBbox(storeys);
  if (!bbox) return false;

  const building = getBuildingFootprintLngLatBounds(location, bbox);
  const west = viewport.getWest();
  const east = viewport.getEast();
  const south = viewport.getSouth();
  const north = viewport.getNorth();
  const lngPad = (east - west) * VIEWPORT_EDGE_INSET_FRACTION;
  const latPad = (north - south) * VIEWPORT_EDGE_INSET_FRACTION;

  const viewWest = west + lngPad;
  const viewEast = east - lngPad;
  const viewSouth = south + latPad;
  const viewNorth = north - latPad;

  const intersects =
    building.east >= viewWest &&
    building.west <= viewEast &&
    building.north >= viewSouth &&
    building.south <= viewNorth;

  return !intersects;
};

/**
 * Camera that centers on the footprint with a medium amount of surrounding context.
 * Pass every building's storeys so Show frames the whole site.
 */
export const getBuildingFocusCamera = ({
  location,
  storeys,
  containerWidth,
  containerHeight,
  paddingFactor = MEDIUM_BUILDING_FOCUS_PADDING_FACTOR,
}: BuildingFocusCameraInput): BuildingFocusCamera | undefined => {
  const bbox = getBuildingFootprintBbox(storeys);
  if (!bbox) return undefined;

  const centerMetric: [number, number] = [
    (bbox.minX + bbox.maxX) / 2,
    (bbox.minY + bbox.maxY) / 2,
  ];
  const centerCoord = convertMetricOffsetToCoordinate(location, centerMetric);
  const zoom = computeBuildingFitZoom({
    containerWidth,
    containerHeight,
    latitudeDegrees: centerCoord.lat,
    bbox,
    paddingFactor,
  });
  if (zoom === undefined || !Number.isFinite(zoom)) return undefined;

  return {
    center: [centerCoord.lng, centerCoord.lat],
    zoom,
  };
};
